import React from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Visibility,
  Edit,
  Delete,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  HourglassEmpty,
  Block,
  Person as PersonIcon,
} from '@mui/icons-material';
import SyncIcon from '@mui/icons-material/Sync';
import SlotCard from './SlotCard';

const OffresTable = ({
  offres,
  searchTerm,
  expandedRow,
  onToggleRow,
  onOpenDrawer,
  onEditOffre,
  onSyncOffre,
  onDeleteClick,
  onUpdateSlotStatus,
}) => {
  const filteredOffres = offres.filter((offre) => {
    const titre = offre.info_offre?.titre_offre?.toLowerCase() || '';
    const nomIntervenant = offre.intervenant?.nom_user?.toLowerCase() || '';
    const prenomIntervenant =
      offre.intervenant?.prenom_user?.toLowerCase() || '';
    const recherche = searchTerm.toLowerCase();

    return (
      titre.includes(recherche) ||
      nomIntervenant.includes(recherche) ||
      prenomIntervenant.includes(recherche)
    );
  });

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        mt: 4,
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
      }}
    >
      <Table>
        <TableHead sx={{ bgcolor: '#F8FAFC' }}>
          <TableRow>
            <TableCell
              sx={{
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                py: 2,
              }}
            >
              Titre
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                fontSize: '0.75rem',
              }}
            >
              Praticien / Intervenant
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                fontSize: '0.75rem',
              }}
            >
              Description
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                fontSize: '0.75rem',
              }}
            >
              Tarifs (Prestation / SOA)
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                fontSize: '0.75rem',
              }}
            >
              Statut
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                fontSize: '0.75rem',
              }}
            >
              Validation
            </TableCell>
            <TableCell
              align="right"
              sx={{
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                fontSize: '0.75rem',
              }}
            >
              Actions
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {filteredOffres.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Aucune offre ne correspond à votre recherche.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredOffres.map((offre) => {
              const info = offre.info_offre;
              const isExpanded = expandedRow === offre.id_offre;

              const intervenant = offre.intervenant;
              const nomComplet = intervenant
                ? `${intervenant.prenom_user || ''} ${intervenant.nom_user || ''}`.trim()
                : 'Non assigné';

              const roleUser = intervenant?.role_user;
              let libelleRole = 'Intervenant';
              if (roleUser === 4) {
                libelleRole = 'Praticien';
              } else if (roleUser === 3) {
                libelleRole = 'Intervenant';
              } else if (intervenant?.role_label) {
                libelleRole =
                  intervenant.role_label.charAt(0).toUpperCase() +
                  intervenant.role_label.slice(1);
              }

              return (
                <React.Fragment key={offre.id_offre}>
                  <TableRow
                    hover
                    sx={{
                      '& > *': { borderBottom: 'unset !important' },
                      bgcolor: isExpanded
                        ? 'rgba(67, 24, 255, 0.02)'
                        : 'inherit',
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600, color: '#1B2559' }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <IconButton
                          size="small"
                          onClick={() => onToggleRow(offre.id_offre)}
                          sx={{
                            color: '#4318FF',
                            bgcolor: isExpanded ? '#F4F7FE' : 'transparent',
                          }}
                        >
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                        {info?.titre_offre}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ color: '#4A5568' }}>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          flexWrap="wrap"
                        >
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="#1B2559"
                          >
                            {nomComplet}
                          </Typography>
                          <Chip
                            label={libelleRole}
                            size="small"
                            sx={{
                              height: '18px',
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              bgcolor:
                                libelleRole === 'Praticien'
                                  ? '#EBF8FF'
                                  : '#F7FAFC',
                              color:
                                libelleRole === 'Praticien'
                                  ? '#2B6CB0'
                                  : '#4A5568',
                              border: '1px solid',
                              borderColor:
                                libelleRole === 'Praticien'
                                  ? '#BEE3F8'
                                  : '#E2E8F0',
                            }}
                          />
                        </Box>
                        {intervenant?.email_user && (
                          <Typography variant="caption" color="text.secondary">
                            {intervenant.email_user}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={
                          <Box
                            component="span"
                            sx={{
                              display: 'block',
                              maxWidth: '150px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {info?.description_offre}
                          </Box>
                        }
                        variant="outlined"
                        size="small"
                        sx={{
                          borderRadius: '6px',
                          fontWeight: 500,
                          borderColor: '#E2E8F0',
                          height: 'auto',
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="#1B2559"
                        >
                          Prestation :{' '}
                          {info?.prix_offre ? `${info.prix_offre} €` : '—'}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="primary.main"
                          fontWeight={600}
                        >
                          SOA :{' '}
                          {info?.prix_special_soa
                            ? `${info.prix_special_soa} €`
                            : '40.00 €'}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={info?.status ? 'Actif' : 'Inactif'}
                        sx={{
                          fontWeight: 700,
                          borderRadius: '8px',
                          bgcolor: info?.status ? '#E6FFFA' : '#F7FAFC',
                          color: info?.status ? '#047857' : '#718096',
                        }}
                        size="small"
                      />
                    </TableCell>

                    <TableCell>
                      <Box display="flex" flexDirection="column" gap={1}>
                        {(() => {
                          const statusValide = offre.is_valide;
                          let label = 'Offre : En attente';
                          let bgcolor = '#FEF3C7';
                          let color = '#92400E';
                          let icon = <HourglassEmpty fontSize="small" />;
                          let iconColor = '#D97706';

                          if (statusValide === 'valider') {
                            label = 'Offre : Validée';
                            bgcolor = '#DEF7EC';
                            color = '#03543F';
                            icon = <CheckCircle fontSize="small" />;
                            iconColor = '#0E9F6E';
                          } else if (statusValide === 'refuser') {
                            label = 'Offre : Refusée';
                            bgcolor = '#FDF1F2';
                            color = '#9B1C1C';
                            icon = <Block fontSize="small" />;
                            iconColor = '#F05252';
                          }

                          return (
                            <Chip
                              icon={icon}
                              label={label}
                              sx={{
                                fontWeight: 700,
                                borderRadius: '8px',
                                bgcolor: bgcolor,
                                color: color,
                                justifyContent: 'flex-start',
                                '& .MuiChip-icon': {
                                  color: iconColor,
                                },
                              }}
                              size="small"
                            />
                          );
                        })()}

                        {(() => {
                          const financialStatus = info?.is_validated_financial;
                          let label = 'Financier : En attente';
                          let bgcolor = '#F3F4F6';
                          let color = '#374151';
                          let icon = <HourglassEmpty fontSize="small" />;
                          let iconColor = '#6B7280';

                          if (
                            financialStatus === 1 ||
                            financialStatus === '1'
                          ) {
                            label = 'Financier : Validé';
                            bgcolor = '#E0F2FE';
                            color = '#0369A1';
                            icon = <CheckCircle fontSize="small" />;
                            iconColor = '#0284C7';
                          }

                          return (
                            <Chip
                              icon={icon}
                              label={label}
                              sx={{
                                fontWeight: 700,
                                borderRadius: '8px',
                                bgcolor: bgcolor,
                                color: color,
                                justifyContent: 'flex-start',
                                '& .MuiChip-icon': {
                                  color: iconColor,
                                },
                              }}
                              size="small"
                            />
                          );
                        })()}
                      </Box>
                    </TableCell>

                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end" gap={0.5}>
                        <IconButton
                          size="small"
                          sx={{ color: '#A3AED0' }}
                          onClick={() => onOpenDrawer(offre)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{ color: '#A3AED0' }}
                          onClick={() => onEditOffre(offre)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => onSyncOffre(offre)}
                          sx={{
                            color: '#4285F4',
                            '&:hover': {
                              bgcolor: '#EFF6FF',
                              transform: 'rotate(180deg)',
                              transition: 'transform 0.4s ease',
                            },
                          }}
                          title="Synchroniser avec Google Calendar"
                        >
                          <SyncIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          sx={{ opacity: 0.8 }}
                          onClick={() => onDeleteClick(offre)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* ROW EXPAND */}
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{
                        p: 0,
                        borderBottom: isExpanded ? '1px solid #E2E8F0' : 'none',
                      }}
                    >
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box
                          sx={{
                            p: 0,
                            bgcolor: '#FFFFFF',
                            borderTop: '1px solid #F1F5F9',
                            maxHeight: '500px',
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': { width: '5px' },
                            '&::-webkit-scrollbar-thumb': {
                              bgcolor: '#D1FAE5',
                              borderRadius: '10px',
                            },
                            '&:hover::-webkit-scrollbar-thumb': {
                              bgcolor: '#10B981',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              p: 3,
                              pb: 2,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              position: 'sticky',
                              top: 0,
                              bgcolor: 'rgba(255, 255, 255, 0.95)',
                              backdropFilter: 'blur(8px)',
                              zIndex: 10,
                              borderBottom: '1px solid #F1F5F9',
                            }}
                          >
                            <Box>
                              <Typography
                                variant="subtitle1"
                                sx={{ color: '#064E3B', fontWeight: 800 }}
                              >
                                Planning des Disponibilités & Réservations
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Suivi des créneaux et des réservations clients
                              </Typography>
                            </Box>
                            <Chip
                              label={`${offre.availability?.length || 0} Périodes actives`}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                bgcolor: '#ECFDF5',
                                color: '#047857',
                                border: '1px solid #D1FAE5',
                              }}
                            />
                          </Box>

                          <Box sx={{ p: 3 }}>
                            {offre.availability?.map((avail) => (
                              <Box key={avail.id_dispo} sx={{ mb: 5 }}>
                                <Box sx={{ mb: 3 }}>
                                  <Typography
                                    variant="overline"
                                    sx={{
                                      color: '#10B981',
                                      fontWeight: 900,
                                      letterSpacing: 1.2,
                                    }}
                                  >
                                    {avail.titre_dispo}
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      color: '#1E293B',
                                      fontWeight: 700,
                                      mt: -0.5,
                                    }}
                                  >
                                    {new Date(
                                      avail.date_debut
                                    ).toLocaleDateString('fr-FR', {
                                      month: 'long',
                                      year: 'numeric',
                                    })}
                                  </Typography>
                                </Box>

                                <Box
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns:
                                      'repeat(auto-fill, minmax(240px, 1fr))',
                                    gap: 2,
                                  }}
                                >
                                  {avail.reserved_slots?.map((slot) => {
                                    const reservationObj =
                                      offre.offre_reservation?.find(
                                        (res) =>
                                          res.id_offre_reservation ===
                                          slot.id_offre_reservation
                                      );
                                    const client = reservationObj?.client;
                                    const nomClient = client
                                      ? `${client.prenom_client || ''} ${client.nom_client || ''}`.trim()
                                      : null;

                                    // Indique si le slot est verrouillé (réservé par un client)
                                    const isReserved = Boolean(
                                      slot.id_offre_reservation || nomClient
                                    );

                                    return (
                                      <Box
                                        key={slot.id_reserved_slot}
                                        sx={{
                                          display: 'flex',
                                          flexDirection: 'column',
                                          height: '100%',
                                        }}
                                      >
                                        <SlotCard
                                          slot={{
                                            ...slot,
                                            clientName: nomClient,
                                            clientEmail: client?.email_client, // ou emailClient selon votre variable
                                            //clientPhone: client?.phone_client, // ou telClient selon votre variable
                                            clientId:
                                              client?.id ||
                                              client?.id_client ||
                                              client?.id_patient_profile,
                                            patientId:
                                              client?.id_patient_profile, // Optionnel si vous gérez les deux
                                          }}
                                          // Désactive la modification/suppression si le slot est réservé
                                          readOnly={isReserved}
                                          onUpdateSlotStatus={
                                            isReserved
                                              ? undefined
                                              : onUpdateSlotStatus
                                          }
                                        />

                                        {/* Coordonnées détaillées supplémentaires si réservé */}
                                        {/*  {nomClient && (
                                          <Box
                                            sx={{
                                              mt: 1,
                                              p: 1.5,
                                              bgcolor: '#F8FAFC',
                                              border: '1px solid #E2E8F0',
                                              borderRadius: '8px',
                                              display: 'flex',
                                              flexDirection: 'column',
                                              gap: 0.5,
                                            }}
                                          >
                                            <Box display="flex" alignItems="center" gap={1}>
                                              <PersonIcon sx={{ fontSize: '1rem', color: '#3B82F6' }} />
                                              <Typography variant="body2" fontWeight={700} color="#1E293B">
                                                {nomClient}
                                              </Typography>
                                            </Box>
                                            {client?.email_client && (
                                              <Typography variant="caption" color="text.secondary">
                                                {client.email_client}
                                              </Typography>
                                            )}
                                            {client?.phone_client && (
                                              <Typography variant="caption" color="text.secondary">
                                                Tél : {client.phone_client}
                                              </Typography>
                                            )}
                                          </Box>
                                        )} */}
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default OffresTable;
